import axios from 'axios';
import { API_CONFIG } from '../config/api';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
  withCredentials: false,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

export const reportService = {
  // Get all reports for a specific patient
  getReportsByPatient: async (patientId) => {
    try {
      const endpoint = `/reports/patient/${patientId}`;
      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Get reports by patient error:', error);
      // Return empty array instead of throwing error to prevent breaking the UI
      return [];
    }
  },

  // Get all reports for a user
  getAllReports: async () => {
    try {
      const endpoint = '/reports';
      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Get all reports error:', error);
      throw error;
    }
  },

  // Get single report by ID
  getReportById: async (id) => {
    try {
      const endpoint = `/reports/${id}`;
      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Get report by ID error:', error);
      throw error;
    }
  },

  // Generate report for a patient
  generateReport: async (patientId) => {
    try {
      const endpoint = `/reports/generate/${patientId}`;
      const response = await api.post(endpoint);
      return response.data;
    } catch (error) {
      console.error('Generate report error:', error);
      throw error;
    }
  },

  // Get reports for a specific user
  getReportsByUser: async (userId) => {
    try {
      const endpoint = `/reports/user/${userId}`;
      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Get reports by user error:', error);
      throw error;
    }
  },

  // Get presigned download URL for report
  getReportDownloadUrl: async (reportId) => {
    try {
      const endpoint = `/reports/${reportId}/download-url`;
      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Get report download URL error:', error);
      throw error;
    }
  },

  // Download report (using presigned URL for private S3 buckets)
  downloadReport: async (reportId) => {
    try {
      // Get presigned URL from backend
      const response = await reportService.getReportDownloadUrl(reportId);
      if (response.success && response.data.url) {
        // Open presigned URL in new window
        window.open(response.data.url, '_blank');
        return { success: true };
      }
      throw new Error('Failed to get download URL');
    } catch (error) {
      console.error('Download report error:', error);
      throw error;
    }
  },

  // View report (using presigned URL for private S3 buckets)
  viewReport: async (reportId) => {
    try {
      // Get presigned URL from backend
      const response = await reportService.getReportDownloadUrl(reportId);
      if (response.success && response.data.url) {
        // Open presigned URL in new window
        window.open(response.data.url, '_blank');
        return { success: true };
      }
      throw new Error('Failed to get view URL');
    } catch (error) {
      console.error('View report error:', error);
      throw error;
    }
  },

  // Update report content
  updateReport: async (reportId, updateData) => {
    try {
      const endpoint = `/reports/${reportId}`;
      const response = await api.put(endpoint, updateData);
      return response.data;
    } catch (error) {
      console.error('Update report error:', error);
      throw error;
    }
  },

  // Submit AI draft for clinical review
  submitForReview: async (reportId) => {
    try {
      const response = await api.post(`/reports/${reportId}/submit-review`);
      return response.data;
    } catch (error) {
      console.error('Submit for review error:', error);
      throw error;
    }
  },

  // Sign and lock report (immutable clinical record)
  signAndLock: async (reportId) => {
    try {
      const response = await api.post(`/reports/${reportId}/sign`);
      return response.data;
    } catch (error) {
      console.error('Sign and lock error:', error);
      throw error;
    }
  },
};

export default reportService;